import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import type { RoutableProps } from 'preact-router';
import { Sparkles, BookOpen, AlertCircle } from 'lucide-preact';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { saveStory } from '../lib/storage';
import type { Story } from '../lib/types';

const LOADING_MESSAGES = [
  'Payment confirmed...',
  'Creating your story...',
  'Adding magical details...',
  'Almost ready...',
];

interface SuccessProps extends RoutableProps {
  session_id?: string;
}

export function Success({ session_id }: SuccessProps) {
  const [status, setStatus] = useState<'loading' | 'processing' | 'ready' | 'error'>('loading');
  const [story, setStory] = useState<Story | null>(null);
  const [error, setError] = useState('');
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0]);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!session_id) {
      setStatus('error');
      setError('Invalid session. Please try creating a new story.');
      return;
    }

    let messageIndex = 0;
    const messageInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length;
      setLoadingMessage(LOADING_MESSAGES[messageIndex]);
    }, 2000);

    const checkStatus = async () => {
      try {
        const response = await fetch(`/api/story-status?session_id=${session_id}`);
        const data = await response.json() as {
          status: 'pending' | 'processing' | 'ready' | 'error';
          story?: Story;
          message?: string;
          error?: string;
        };

        if (data.error) {
          setStatus('error');
          setError(data.error);
          clearInterval(messageInterval);
          return;
        }

        if (data.status === 'ready' && data.story) {
          setStatus('ready');
          setStory(data.story);
          // Save to local storage
          saveStory(data.story);
          clearInterval(messageInterval);
          return;
        }

        if (data.status === 'pending') {
          setStatus('error');
          setError('Payment was not completed. Please try again.');
          clearInterval(messageInterval);
          return;
        }

        // Still processing, keep polling
        setStatus('processing');
        setPollCount((prev) => prev + 1);
      } catch (err) {
        console.error('Status check error:', err);
        setPollCount((prev) => prev + 1);
      }
    };

    // Initial check
    checkStatus();

    // Poll every 2 seconds until ready or max attempts
    const pollInterval = setInterval(() => {
      if (pollCount < 30) {
        // Max 60 seconds of polling
        checkStatus();
      } else {
        clearInterval(pollInterval);
        clearInterval(messageInterval);
        setStatus('error');
        setError('Story generation is taking longer than expected. Please check your library in a few minutes.');
      }
    }, 2000);

    return () => {
      clearInterval(pollInterval);
      clearInterval(messageInterval);
    };
  }, [session_id, pollCount]);

  if (status === 'loading' || status === 'processing') {
    return (
      <div className="min-h-screen gradient-warm flex items-center justify-center">
        <div className="text-center animate-in max-w-md mx-auto px-4">
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 w-32 h-32 rounded-full bg-gold/20 blur-xl pulse-ring" />
            <div className="relative w-32 h-32 rounded-full bg-parchment border-ornate flex items-center justify-center">
              <Sparkles className="w-14 h-14 text-gold float" strokeWidth={1.25} />
            </div>
          </div>
          <LoadingSpinner message={loadingMessage} />
          <p className="mt-4 text-ink/40 text-sm">This usually takes about 10-15 seconds</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen gradient-warm flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 rounded-full bg-rust/10 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-rust" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl font-display font-medium text-ink mb-4">Something went wrong</h1>
          <p className="text-ink/60 mb-8">{error}</p>
          <button
            onClick={() => route('/')}
            className="px-8 py-4 bg-rust text-cream rounded-2xl font-medium
                     shadow-story hover:shadow-story-hover transition-all cursor-pointer"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (status === 'ready' && story) {
    return (
      <div className="min-h-screen gradient-warm flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4 animate-in">
          <div className="w-24 h-24 rounded-full bg-moss/10 flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-12 h-12 text-moss" strokeWidth={1.25} />
          </div>
          <h1 className="text-3xl font-display font-medium text-ink mb-4">Your Story is Ready</h1>
          <p className="text-xl text-ink/70 mb-2 font-display">{story.title}</p>
          <p className="text-ink/50 mb-8">
            Starring {story.choices.childName} the {story.choices.animal}
          </p>
          <button
            onClick={() => route(`/read/${story.id}`)}
            className="px-8 py-4 bg-rust text-cream rounded-2xl font-medium text-lg
                     shadow-story hover:shadow-story-hover transition-all cursor-pointer
                     flex items-center gap-3 mx-auto"
          >
            <BookOpen size={20} strokeWidth={1.5} />
            Read Your Story
          </button>
        </div>
      </div>
    );
  }

  return null;
}
